<template>
    <Tab
        v-if="loaded"
        id="marketSwitchEl"
        v-model:value="marketPage"
        tabStyle="tab-common"
        v-bind:tabList="formattedTabItems"
    />
</template>

<script lang="ts">
    import pickBy from 'lodash/pickBy';
    import { storeToRefs } from 'pinia';
    import { computed, defineComponent, onMounted, watch } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import Tab from '@/components/common/Tab.vue';
    import type { IPopoverItem, ITabItem } from '@/interface/Tab';
    import { MarketPage } from '@/interface/enum';
    import useScroll from '@/libraries/composables/useScroll';
    import { useZone } from '@/libraries/composables/useZone';
    import { getSportTypeKey } from '@/libraries/helpers/sportsHelper';
    import { i18n } from '@/libraries/i18n';
    import { useEventsCountInfoQuery } from '@/libraries/oddsApi/composables/useEventsCountInfoQuery';
    import { getDateAfterTodayWithMonth, getWeekday } from '@/libraries/utils/date';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';
    import { useOddsApiStore } from '@/stores/oddsApiStore';
    import { useRootStore } from '@/stores/rootStore';

    export default defineComponent({
        name: 'MarketSwitch',
        components: {
            Tab,
        },
        setup() {
            onMounted(() => {
                const { initHeaderDisplayHeight } = useScroll();
                initHeaderDisplayHeight(() => document.getElementById('marketSwitchEl'), 'marketSwitchHeight');
            });
            const { isShowLiveInToday, isEuroSite } = storeToRefs(useFeatureToggleStore());
            const { customerSettings } = storeToRefs(useOddsApiStore());
            const { sportType, marketPage, isMixParlayMode } = storeToRefs(useRootStore());
            const { updateMarketPage } = useRootStore();

            const route = useRoute();
            const { isInAnyZone, leagueNames, leagueIds, isTournamentCenterZone } = useZone();

            const isIncludeLiveInToday = computed(() => isShowLiveInToday.value && isEuroSite.value);
            const { eventsCountInfo, loaded } = useEventsCountInfoQuery({
                sportType,
                isMixParlayMode,
                isIncludeLiveInToday,
                tournamentIds: leagueIds,
                tournamentNames: leagueNames,
            });

            const isSmallWidthScreen = window.innerWidth < 360;

            const isEarlyMarketHasDropdown = computed(() => !isEuroSite.value);

            const filterSportEventTabCount = computed<Partial<Record<MarketPage, number>>>(() => {
                const sportsEventsName = [MarketPage.Live, MarketPage.Outright, MarketPage.EarlyMarket, MarketPage.Today].map(x => x.toString());
                return pickBy(eventsCountInfo.value, (_, key) => sportsEventsName.includes(key.toString()));
            });

            const day1Label = computed(() => i18n.t(`sportsPage_filterDate_day${getWeekday(1, 8)}`));
            const day2Label = computed(() => i18n.t(`sportsPage_filterDate_day${getWeekday(2, 8)}`));
            const day3Label = computed(() => i18n.t(`sportsPage_filterDate_day${getWeekday(3, 8)}`));

            const gaPrefix = computed(() => (isTournamentCenterZone.value ? 'TC_Matches' : 'JARVIS'));

            const earlyMarketPopoverList = computed<IPopoverItem[]>(() => [
                {
                    value: MarketPage.EarlyMarket,
                    title: isSmallWidthScreen
                        ? i18n.t('marketPage_EarlyMarket_short')
                        : i18n.t('marketPage_EarlyMarket'),
                    shortTitle: null,
                    tabGA: `${gaPrefix.value}_MarketType_DropDown_EarlyMarket_Early`,
                    count: eventsCountInfo.value[MarketPage.EarlyMarket],
                    disabled: false,
                },
                {
                    value: MarketPage.EarlyMarketPlus1,
                    shortTitle: getDateAfterTodayWithMonth(1, 8),
                    title: `${getDateAfterTodayWithMonth(1, 8)} ${day1Label.value}`,
                    tabGA: `${gaPrefix.value}_MarketType_DropDown_EarlyMarket_1DayLater`,
                    count: eventsCountInfo.value[MarketPage.EarlyMarketPlus1],
                    disabled: eventsCountInfo.value[MarketPage.EarlyMarketPlus1] <= 0,
                },
                {
                    value: MarketPage.EarlyMarketPlus2,
                    shortTitle: getDateAfterTodayWithMonth(2, 8),
                    title: `${getDateAfterTodayWithMonth(2, 8)} ${day2Label.value}`,
                    tabGA: `${gaPrefix.value}_MarketType_DropDown_EarlyMarket_2DaysLater`,
                    count: eventsCountInfo.value[MarketPage.EarlyMarketPlus2],
                    disabled: eventsCountInfo.value[MarketPage.EarlyMarketPlus2] <= 0,
                },
                {
                    value: MarketPage.EarlyMarketPlus3,
                    shortTitle: getDateAfterTodayWithMonth(3, 8),
                    title: `${getDateAfterTodayWithMonth(3, 8)} ${day3Label.value}`,
                    tabGA: `${gaPrefix.value}_MarketType_DropDown_EarlyMarket_3DaysLater`,
                    count: eventsCountInfo.value[MarketPage.EarlyMarketPlus3],
                    disabled: eventsCountInfo.value[MarketPage.EarlyMarketPlus3] <= 0,
                },
                {
                    value: MarketPage.EarlyMarketPlus4Over,
                    title: i18n.t('sportsPage_filter_laterDates'),
                    shortTitle: null,
                    tabGA: `${gaPrefix.value}_MarketType_DropDown_EarlyMarket_LaterDates`,
                    count: eventsCountInfo.value[MarketPage.EarlyMarketPlus4Over],
                    disabled: eventsCountInfo.value[MarketPage.EarlyMarketPlus4Over] <= 0,
                },
            ]);

            const formattedTabItems = computed<ITabItem[]>(() => Object.keys(filterSportEventTabCount.value).map((_tab: string) => {
                const tab = _tab as MarketPage;
                return ({
                    value: MarketPage[tab],
                    title: i18n.t(getTabTitle(MarketPage[tab])),
                    count: filterSportEventTabCount.value[tab],

                    tabGA: MarketPage[tab] === MarketPage.EarlyMarket
                        ? `${gaPrefix.value}_MarketType_DropDown_EarlyMarket`
                        : `${gaPrefix.value}_MarketType_Tab_${tab}`,

                    popoverList:
                        (isEarlyMarketHasDropdown.value && MarketPage[tab] === MarketPage.EarlyMarket) ? earlyMarketPopoverList.value : undefined,
                });
            }));

            function adjustDefaultPage() {
                const marketPagesHasCount = formattedTabItems.value
                    .find(item => item.value === marketPage.value && (item.count ?? 0) > 0);
                if (!marketPagesHasCount) {
                    const itemWithCount = formattedTabItems.value.find(item => (item.count ?? 0) > 0);
                    if (itemWithCount) {
                        updateMarketPage(itemWithCount.value as MarketPage);
                    }
                }
            }

            function getTabTitle(tab: MarketPage) {
                const euroSiteSpecial = [MarketPage.Live, MarketPage.EarlyMarket];

                if (isEuroSite.value && euroSiteSpecial.includes(tab)) {
                    return `marketPage_${tab}_EURO`;
                }

                const smallWidthScreenSpecial = [MarketPage.EarlyMarket];
                if (isSmallWidthScreen && smallWidthScreenSpecial.includes(tab)) {
                    return `marketPage_${tab}_short`;
                }

                return `marketPage_${tab}`;
            }

            watch(loaded, () => {
                if (loaded.value) {
                    adjustDefaultPage();
                }
            });

            const router = useRouter();
            watch(() => route, async () => {
                const {
                    toSport: toSportIdRaw,
                    toLeague: toLeagueIdRaw,
                    ...remainingQuery
                } = route.query;

                if (toSportIdRaw || toLeagueIdRaw) {
                    router.replace({
                        path: route.path,
                        query: remainingQuery,
                    });
                }

                if (toSportIdRaw && typeof toSportIdRaw === 'string') {
                    const sportType = parseInt(atob(unescape(toSportIdRaw)));
                    if (getSportTypeKey(sportType)) {
                        useRootStore().updateSportType(sportType);
                    }
                }
            }, { immediate: true });

            watch(loaded, (newValue) => {
                if (newValue) {
                    const queryPage = route.query.page;
                    if (queryPage !== undefined && Object.values(MarketPage).includes(queryPage as MarketPage)) {
                        updateMarketPage(queryPage as MarketPage);
                        return;
                    }

                    if (isInAnyZone.value) {
                        if (marketPage.value === MarketPage.Outright) {
                            return;
                        }

                        const eventsCountIndex = Object.values(eventsCountInfo.value).findIndex(x => x > 0);
                        const marketPageMapping: Record<number, MarketPage> = {
                            0: MarketPage.Live,
                            1: MarketPage.Today,
                            2: MarketPage.EarlyMarket,
                            3: MarketPage.Outright,
                        };
                        updateMarketPage(marketPageMapping[eventsCountIndex]);
                        return;
                    }

                    adjustDefaultPage();
                }
            });

            return {
                isInAnyZone,
                leagueNames,
                leagueIds,
                isEuroSite,
                isShowLiveInToday,
                customerSettings,
                sportType,
                isSmallWidthScreen,
                marketPage,
                formattedTabItems,
                loaded,
            };
        },
    });
</script>
